import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  makeStyles,
} from "@material-ui/core";
import { X, Play, DollarSign, CreditCard } from "lucide-react";
import CCBillPayment, { PaymentTokenResponse } from "./CCBillPayment";
import { makeOneClickPayment, getAllTokens } from "../../api/ccbillApi";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  paper: {
    backgroundColor: "#1A1A1A",
    borderRadius: "12px",
    border: "1px solid #2A2A2A",
    width: "551px",
    maxWidth: "551px",
    margin: "16px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      margin: "8px",
      width: "100%",
      maxWidth: "calc(100% - 16px)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 20px 0 20px",
  },
  title: {
    color: "#FFFFFF",
    fontSize: "32px",
    fontWeight: 600,
    lineHeight: 1.2,
    fontFamily: "Work Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  closeBtn: {
    color: "#FFFFFF",
    padding: 8,
  },
  content: {
    padding: "16px 20px 24px",
  },
  previewWrap: {
    position: "relative",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#0C0A09",
    margin: "0 auto 20px",
    width: "100%",
    maxWidth: "280px",
    aspectRatio: "1",
    maxHeight: "280px",
    [theme.breakpoints.down("xs")]: {
      maxHeight: "240px",
      maxWidth: "min(280px, 100%)",
    },
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    filter: "blur(22px)",
    transform: "scale(1.08)",
  },
  playBadge: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  priceOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 16,
    pointerEvents: "none",
  },
  dollarCircle: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(255,255,255,0.25)",
  },
  priceLine: {
    color: "#FFFFFF",
    fontSize: "15px",
    fontWeight: 600,
    textAlign: "center",
    lineHeight: 1.35,
    fontFamily: "Work Sans, sans-serif",
    textShadow: "0 1px 8px rgba(0,0,0,0.8)",
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: "8px",
    textAlign: "center",
    fontFamily: "Work Sans, sans-serif",
  },
  sectionSub: {
    color: "#FFFFFFCC",
    fontSize: "16px",
    marginBottom: "20px",
    textAlign: "center",
    fontFamily: "Work Sans, sans-serif",
  },
  oneClickBtn: {
    backgroundColor: "#FE5A97",
    color: "#FFFFFF",
    width: "100%",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "14px",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "12px",
    fontFamily: "Work Sans, sans-serif",
    "&:hover": {
      backgroundColor: "#FF6BA8",
    },
  },
  paymentRoot: {
    padding: 0,
    margin: 0,
    maxWidth: "100%",
    background: "transparent",
    border: "none",
  },
}));

export interface UnlockPaidContentModalProps {
  open: boolean;
  onClose: () => void;
  previewUrl: string;
  isVideo: boolean;
  price: number;
  userId: string;
  email: string;
  postId: string;
  tipForUserId: string;
  onProceed: () => void;
  onPaymentSuccess: () => void;
}

const UnlockPaidContentModal: React.FC<UnlockPaidContentModalProps> = ({
  open,
  onClose,
  previewUrl,
  isVideo,
  price,
  userId,
  email,
  postId,
  tipForUserId,
  onProceed,
  onPaymentSuccess,
}) => {
  const classes = useStyles();
  const [last4, setLast4] = React.useState<string | null>(null);
  const [hasToken, setHasToken] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    getAllTokens()
      .then((response: PaymentTokenResponse) => {
        const active = response.cards.find(t => t.isEnabled);
        if (active) {
          setLast4(active.last4);
          setHasToken(true);
        } else {
          setHasToken(false);
        }
      })
      .catch(() => setHasToken(false));
  }, [open]);

  const handleOneClickPayment = async () => {
    if (!price) return;
    try {
      const tokens: PaymentTokenResponse = await getAllTokens();
      const activeToken = tokens.cards.find(t => t.isEnabled);
      if (!activeToken) {
        toast.error("No active card available");
        return;
      }
      const payload = {
        recipientId: userId || null,
        amount: price,
        context: "post-purchase" as const,
        postId,
        messageId: undefined as string | undefined,
        creatorId: tipForUserId,
        description: "",
        tokenId: activeToken.id,
      };
      await makeOneClickPayment(payload as any);
      onPaymentSuccess();
    } catch (error) {
      console.error("One-click payment failed:", error);
      toast.error(
        (error as any)?.response?.data?.message || "One-click payment failed."
      );
    }
  };

  const mediaLabel = isVideo ? "video" : "image";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
    >
      <Box className={classes.header}>
        <Typography component="h2" className={classes.title}>
          Unlock paid content
        </Typography>
        <IconButton
          className={classes.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={22} />
        </IconButton>
      </Box>
      <DialogContent className={classes.content}>
        <Box className={classes.previewWrap}>
          <img
            src={previewUrl}
            alt=""
            className={classes.previewImg}
            draggable={false}
          />
          {isVideo && (
            <Box className={classes.playBadge}>
              <Play size={18} color="#fff" fill="#fff" />
            </Box>
          )}
          <Box className={classes.priceOverlay}>
            <Box className={classes.dollarCircle}>
              <DollarSign size={28} color="#fff" strokeWidth={2.5} />
            </Box>
            <Typography className={classes.priceLine}>
              Purchase this {mediaLabel} for ${price}
            </Typography>
          </Box>
        </Box>

        <Typography className={classes.sectionTitle}>
          Purchase exclusive content from this creator
        </Typography>
        <Typography className={classes.sectionSub}>
          Unlocks automatically after payment
        </Typography>

        {hasToken && last4 && (
          <Button
            className={classes.oneClickBtn}
            onClick={handleOneClickPayment}
            startIcon={<CreditCard size={18} color="#fff" />}
          >
            Pay Instantly With **** **** **** {last4}
          </Button>
        )}

        <Box className={classes.paymentRoot}>
          <CCBillPayment
            price={price}
            context="post-purchase"
            title=""
            showPrice={false}
            compactPostPurchase
            buttonText="Pay With CCBill"
            description=""
            onProceed={onProceed}
            onPaymentSuccess={onPaymentSuccess}
            userId={userId}
            email={email}
            postId={postId}
            tipForUserId={tipForUserId}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UnlockPaidContentModal;
